import { Search } from "lucide-react";
import * as React from "react";
import { msg } from "@lingui/core/macro";

import { useDebounce } from "../hooks/use-debounce";
import { cn } from "../lib/cn";
import { useI18n } from "../lib/i18n";

/**
 * WorkspaceSearchBar — single search primitive used by every workspace
 * surface (Conversazioni, Documenti, Finanza, Calendario, Lavoro,
 * Tecnico). Linear + Notion + Granola SOTA pattern (May 2026, updated
 * June 2026 with chip-strip-below layout):
 *
 * Default layout (June 2026 — ``chipStripPosition="below"``)
 *
 *   ┌─ Container 32px (search row) ──────────────────────────────┐
 *   │ 🔍  [type-to-search input]                          ⌘F     │
 *   └────────────────────────────────────────────────────────────┘
 *   ┌─ Chip strip row (no container, transparent gap) ───────────┐
 *   │  [Chip · v]  [Chip · v]  [Chip · v]  [Chip · v]            │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Legacy layout (``chipStripPosition="inline"``) — kept for tabs that
 * specifically need a horizontal-stripe look (none today; deprecate
 * after the June 2026 uniformity pass is complete):
 *
 *   ┌─ Container 32px ───────────────────────────────────────────┐
 *   │ 🔍  [input]   ⌘F   [Chip] [Chip] [Chip]                    │
 *   └────────────────────────────────────────────────────────────┘
 *
 * Behaviour:
 *   - Debounce 200ms before firing ``onSearch`` (reuses the shared
 *     ``useDebounce`` hook so every surface uses identical semantics).
 *   - ``Cmd+F`` (or ``Ctrl+F`` on non-Apple) focuses the input. The
 *     shortcut is opt-out via ``focusShortcut: false``.
 *   - ``Escape`` clears the input AND blurs the field (Linear idiom).
 *   - ``role="search"`` + ``aria-label`` on the container for AT users.
 *
 * DRY discipline: this is the ONLY search-input chrome across the six
 * workspace tabs. Tab-specific filters live in the ``chipStrip`` slot
 * — never branch on tab id inside this primitive.
 */
export type ChipStripPosition = "below" | "inline";


export interface WorkspaceSearchBarProps {
  /** Placeholder text shown when input is empty (Italian). */
  readonly placeholder?: string;
  /**
   * Initial query value — populated from the route's URL search param
   * so deep-links round-trip with the typed query intact.
   */
  readonly initialQuery?: string;
  /** Fires AFTER the debounce timeout with the trimmed string value. */
  readonly onSearch: (query: string) => void;
  /** Debounce delay in milliseconds (default 200). */
  readonly debounceMs?: number;
  /**
   * Keyboard shortcut that focuses the input. ``cmd+f`` mirrors the
   * browser's native find-in-page so the user's muscle memory works.
   * Pass ``false`` to opt out — the chat composer Cmd+K shortcut still
   * works via its own listener.
   */
  readonly focusShortcut?: "cmd+f" | "cmd+k" | false;
  /** ``Escape`` clears query + blurs input (default true). */
  readonly clearOnEscape?: boolean;
  /**
   * Per-tab filter chips. By default rendered on a dedicated ROW BELOW
   * the search input (June 2026 uniformity pass — makes targets bigger
   * + readability higher than the legacy inline-right cluster).
   * Set ``chipStripPosition="inline"`` to restore the historical
   * inline-right behaviour.
   */
  readonly chipStrip?: React.ReactNode;
  /**
   * Where to render :prop:`chipStrip`. ``"below"`` (default) places it
   * on its own row underneath the search container; ``"inline"`` keeps
   * the legacy inline-right cluster.
   */
  readonly chipStripPosition?: ChipStripPosition;
  /**
   * Optional ``aria-label`` override for the search container.
   */
  readonly ariaLabel?: string;
  /** Extra classes merged onto the root wrapper. */
  readonly className?: string;
}


const SHORTCUT_HINT_BY_KEY: Record<"cmd+f" | "cmd+k", string> = {
  "cmd+f": "F",
  "cmd+k": "K",
};


/** True when the active platform uses ⌘ as the meta key. SSR-safe. */
function isAppleHost(): boolean {
  if (typeof navigator === "undefined") return false;
  const platform = navigator.platform || "";
  return /Mac|iPhone|iPad/i.test(platform) || /Mac/i.test(navigator.userAgent);
}


function useFocusShortcut(
  shortcut: "cmd+f" | "cmd+k" | false,
  inputRef: React.RefObject<HTMLInputElement | null>,
): void {
  React.useEffect(() => {
    if (shortcut === false) return;
    if (typeof window === "undefined") return;
    const targetKey = shortcut === "cmd+f" ? "f" : "k";
    const apple = isAppleHost();
    const handler = (event: KeyboardEvent): void => {
      if (event.repeat) return;
      const metaPressed = apple ? event.metaKey : event.ctrlKey;
      if (!metaPressed) return;
      if (event.key.toLowerCase() !== targetKey) return;
      if (event.altKey || event.shiftKey) return;
      event.preventDefault();
      inputRef.current?.focus();
      inputRef.current?.select();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [shortcut, inputRef]);
}


export const WorkspaceSearchBar = React.forwardRef<HTMLInputElement, WorkspaceSearchBarProps>(
  function WorkspaceSearchBar(
    {
      placeholder,
      initialQuery = "",
      onSearch,
      debounceMs = 200,
      focusShortcut = "cmd+f",
      clearOnEscape = true,
      chipStrip,
      chipStripPosition = "below",
      ariaLabel,
      className,
    },
    forwardedRef,
  ) {
    const { i18n } = useI18n();
    const resolvedPlaceholder = placeholder ?? i18n._(msg`Cerca…`);
    const resolvedAriaLabel = ariaLabel ?? i18n._(msg`Cerca nello spazio di lavoro`);
    const [value, setValue] = React.useState<string>(initialQuery);
    const debounced = useDebounce(value, debounceMs);
    const internalRef = React.useRef<HTMLInputElement>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === "function") {
          forwardedRef(node);
        } else if (forwardedRef) {
          forwardedRef.current = node;
        }
      },
      [forwardedRef],
    );

    const lastFired = React.useRef<string>(initialQuery);
    React.useEffect(() => {
      if (debounced === lastFired.current) return;
      lastFired.current = debounced;
      onSearch(debounced);
    }, [debounced, onSearch]);

    React.useEffect(() => {
      if (typeof document === "undefined") return;
      const active = document.activeElement;
      if (active && active === internalRef.current) return;
      setValue(initialQuery);
      lastFired.current = initialQuery;
    }, [initialQuery]);

    useFocusShortcut(focusShortcut, internalRef);

    const handleKeyDown = React.useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (!clearOnEscape) return;
        if (event.key !== "Escape") return;
        if (value.length === 0) {
          internalRef.current?.blur();
          return;
        }
        event.stopPropagation();
        setValue("");
        lastFired.current = "";
        onSearch("");
        internalRef.current?.blur();
      },
      [clearOnEscape, value.length, onSearch],
    );

    const shortcutHint = focusShortcut !== false ? SHORTCUT_HINT_BY_KEY[focusShortcut] : null;
    const shortcutGlyph = React.useMemo(() => (isAppleHost() ? "⌘" : "Ctrl"), []);
    const chipsBelow = chipStripPosition === "below" && Boolean(chipStrip);
    const chipsInline = chipStripPosition === "inline" && Boolean(chipStrip);

    const searchRow = (
      // biome-ignore lint/a11y/useSemanticElements: composite search landmark — see comment
      <div
        role="search"
        aria-label={resolvedAriaLabel}
        className={cn(
          "flex h-8 items-center gap-2 rounded-md border border-hairline bg-cream/85 px-3",
          "backdrop-blur-sm transition-shadow",
          "focus-within:border-transparent focus-within:ring-1 focus-within:ring-ando-orange/40",
        )}
      >
        <Search aria-hidden className="h-4 w-4 shrink-0 text-ink-3" strokeWidth={1.75} />
        <input
          ref={setRefs}
          type="search"
          enterKeyHint="search"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="none"
          spellCheck={false}
          value={value}
          placeholder={resolvedPlaceholder}
          onChange={(event) => setValue(event.target.value)}
          onKeyDown={handleKeyDown}
          aria-keyshortcuts={focusShortcut === false ? undefined : "Meta+F"}
          className={cn(
            "min-w-0 flex-1 bg-transparent text-[13px] text-ink-1",
            "placeholder:text-ink-3",
            "focus:outline-none",
            "[&::-webkit-search-cancel-button]:appearance-none",
          )}
        />
        {shortcutHint && value.length === 0 ? (
          <kbd
            aria-hidden
            className={cn(
              "hidden shrink-0 items-center gap-0.5 rounded border border-hairline bg-white/60",
              "px-1.5 py-0.5 font-mono text-[10px] text-ink-3 sm:inline-flex",
            )}
          >
            <span>{shortcutGlyph}</span>
            <span>{shortcutHint}</span>
          </kbd>
        ) : null}
        {chipsInline ? (
          <div
            className={cn(
              "ml-1 flex shrink-0 items-center gap-1.5",
              "max-w-[60%] overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
            )}
          >
            {chipStrip}
          </div>
        ) : null}
      </div>
    );

    if (!chipsBelow) {
      // Inline (legacy) or no strip — preserve the single-row layout
      // wrapped in the same outer ``className`` slot so callers don't
      // see a behaviour change.
      return <div className={className}>{searchRow}</div>;
    }

    // chip-strip-below — search row + a dedicated chip row underneath.
    return (
      <div className={cn("flex flex-col gap-2", className)}>
        {searchRow}
        <div
          role="group"
          aria-label={i18n._(msg`Filtri di ricerca`)}
          className={cn(
            "flex flex-wrap items-center gap-1.5",
            // Horizontal-scroll fallback on narrow viewports — same
            // pattern as SubTabBar / SmartViewChips so the three rows
            // align visually under the same horizontal rules.
            "overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          )}
        >
          {chipStrip}
        </div>
      </div>
    );
  },
);
